/**
 * useAlerts Hook - Works with ANY backend
 * Frontend-only date filtering
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { alertsApi } from '../services/alertsApi';
import { Alert } from '../types';

interface UseAlertsOptions {
  selectedDate?: string;
  autoLoad?: boolean;
  siteCode?: string;
  autoRefreshMs?: number;
}

// Global cache for alerts keyed by site+date context
let globalAlertsCache: Record<string, Alert[]> = {};
let globalCacheTime: Record<string, number> = {};
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

export const useAlerts = (options: UseAlertsOptions = {}) => {
  const { selectedDate, autoLoad = true, siteCode, autoRefreshMs = 30000 } = options;

  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState<number>(0);

  const isMountedRef = useRef<boolean>(true);
  const isLoadingRef = useRef<boolean>(false);

  const getCacheKey = useCallback(() => {
    const siteKey = (siteCode || 'all').trim();
    const dateKey = (selectedDate || 'all-dates').trim();
    return `${siteKey}::${dateKey}`;
  }, [siteCode, selectedDate]);

  /**
   * Load alerts from backend for current site+date context
   */
  const loadAllAlerts = useCallback(async (forceRefresh = false) => {
    const cacheKey = getCacheKey();
    const isCacheValid = globalAlertsCache[cacheKey] &&
      (Date.now() - (globalCacheTime[cacheKey] || 0)) < CACHE_DURATION;

    if (!forceRefresh && isCacheValid) {
return globalAlertsCache[cacheKey];
    }

    // Prevent duplicate requests
    if (isLoadingRef.current) {
// Wait for current request to finish
      while (isLoadingRef.current) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      return globalAlertsCache[cacheKey] || [];
    }

    isLoadingRef.current = true;

    try {
const response = await alertsApi.fetchAlerts(siteCode, selectedDate);

      if (!isMountedRef.current) return [];
// Update global cache
      globalAlertsCache[cacheKey] = response.alerts;
      globalCacheTime[cacheKey] = Date.now();

      return globalAlertsCache[cacheKey];

    } catch (err: any) {
      console.error('Error loading alerts:', err);
      throw err;

    } finally {
      isLoadingRef.current = false;
    }
  }, [getCacheKey, selectedDate, siteCode]);

  /**
   * Main effect: Load and filter alerts
   */
  useEffect(() => {
    if (!autoLoad) {
      setLoading(false);
      return;
    }

    const loadAndFilter = async () => {
      setLoading(true);
      setError(null);

      try {
        const allAlerts = await loadAllAlerts(false);

        if (!isMountedRef.current) return;

        setAlerts(allAlerts);
        setTotalCount(allAlerts.length);

      } catch (err: any) {
        if (!isMountedRef.current) return;

        const errorMsg = err?.message || 'Failed to load alerts';
        console.error('Error:', errorMsg);
        setError(errorMsg);
        setAlerts([]);
        setTotalCount(0);

      } finally {
        if (isMountedRef.current) {
          setLoading(false);
        }
      }
    };

    loadAndFilter();

  }, [selectedDate, autoLoad, loadAllAlerts, siteCode]);

  /**
   * Refetch: Force reload from API
   */
  const refetch = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const allAlerts = await loadAllAlerts(true); // Force refresh
      setAlerts(allAlerts);
      setTotalCount(allAlerts.length);
    } catch (err: any) {
      setError(err?.message || 'Failed to refresh');
    } finally {
      setLoading(false);
    }
  }, [loadAllAlerts]);

  // Auto-refresh alerts in foreground without forcing page refresh.
  useEffect(() => {
    if (!autoLoad || autoRefreshMs <= 0) return;

    let timeoutId: number | undefined;
    let isMounted = true;
    let currentInterval = autoRefreshMs;
    let consecutiveErrors = 0;

    const pollAlerts = async () => {
      if (!isMounted) return;

      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') {
        // If not visible, wait and try again
        timeoutId = window.setTimeout(pollAlerts, currentInterval);
        return;
      }

      try {
        const token = sessionStorage.getItem('access_token') ||
                      localStorage.getItem('access_token') ||
                      localStorage.getItem('token') ||
                      sessionStorage.getItem('token');
        if (!token) {
          consecutiveErrors++;
          currentInterval = Math.min(autoRefreshMs * Math.pow(2, consecutiveErrors), 60000);
          if (isMounted) timeoutId = window.setTimeout(pollAlerts, currentInterval);
          return;
        }

        await refetch();
        consecutiveErrors = 0;
        currentInterval = autoRefreshMs;
        
        if (isMounted) {
          timeoutId = window.setTimeout(pollAlerts, currentInterval);
        }
      } catch (error: any) {
        if (error?.message?.includes('401') || error?.status === 401) {
          consecutiveErrors++;
          currentInterval = Math.min(autoRefreshMs * Math.pow(2, consecutiveErrors), 60000);
        } else {
          currentInterval = Math.min(currentInterval + 5000, 60000);
        }
        if (isMounted) {
          timeoutId = window.setTimeout(pollAlerts, currentInterval);
        }
      }
    };

    timeoutId = window.setTimeout(() => {
      if (isMounted) pollAlerts();
    }, currentInterval);

    return () => {
      isMounted = false;
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [autoLoad, autoRefreshMs, refetch]);

  /**
   * Cleanup on unmount
   */
  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  return {
    alerts,
    setAlerts,
    loading,
    error,
    totalCount,
    totalPages: 1,
    refetch,
  };
};

